// Масите не се застъпват: всяка маса (плотът + столовете ѝ) стои на поне
// MIN_TABLE_GAP от съседните маси. Чиста геометрия в логически единици —
// ползват я влаченето (`useElementDrag`), завъртането с дръжката
// (`useElementRotate`) и промените от инспектора/клавишите
// (`useSchemaEditorActions`). Обектите (бар, стена, растение…) не участват —
// правилото е маса срещу маса.
//
// Формата на масата: кръглата е кръг (плотът + венецът столове), квадратната и
// правоъгълната — завъртян правоъгълник (плотът, разширен към страните със
// столове). Застъпването се мери по разделящите оси (SAT): ако по някоя ос
// проекциите на двете маси са на поне MIN_TABLE_GAP една от друга, масите са
// свободни; иначе оста с най-малко застъпване казва накъде и колко да се
// избута масата — така тя се плъзга покрай съседа, вместо да залепне за него.

import type { SchemaElement } from "../../api/restaurantSchema";
import { CHAIR_REACH, GRID_STEP, MIN_TABLE_GAP, tableChairs, tableDimensions } from "./geometry";
import type { LogicalPoint as Point } from "./stageLayout";

/**
 * Формата на масата спрямо центъра ѝ — вече завъртяна, в екранни оси.
 * `reach` е радиусът на кръга около нея (далечните маси се отсяват без SAT),
 * `normals` — нормалите на страните на правоъгълника.
 */
export type TableOutline =
  | { kind: "circle"; radius: number; reach: number }
  | { kind: "polygon"; corners: Point[]; normals: Point[]; reach: number };

/**
 * Съседна маса, в която местената/завъртяната не бива да влезе. `gap` — само за
 * съсед, който и досега е бил по-близо от MIN_TABLE_GAP (стара схема отпреди
 * правилото): колкото е отстоянието сега (при застъпване — отрицателно). Масата
 * не може да го доближи повече, но може да се отдалечи.
 */
export interface TableObstacle {
  element: SchemaElement;
  outline: TableOutline;
  gap?: number;
}

// Застъпване до толкова (логически единици) не се брои: позициите се записват
// като цели числа и закръглянето може да „влезе" с част от сантиметъра.
const OVERLAP_TOLERANCE = 1;
// Под това разстояние две числа са равни (грешка от плаващата запетая).
const EPSILON = 1e-6;
// Наведнъж масата се мести с най-много толкова — по-малко от половината на
// най-тясната маса, затова не може да „прескочи" съседа, колкото и
// бързо да мине пръстът.
const SLIDE_STEP = GRID_STEP;
// Колко пъти подред се избутва от съседите на една стъпка (ъгъл между две маси).
const MAX_PUSHES = 6;
// Колко пъти масата тръгва наново към пръста, след като е заобиколила съсед.
const MAX_SLIDE_PASSES = 3;

// Осите на екрана — по тях мери и свободното място за нов елемент (`findFreeSpot`),
// затова място, което то е дало, тук също е свободно.
const SCREEN_AXES: Point[] = [
  { x: 1, y: 0 },
  { x: 0, y: 1 },
];

const dot = (first: Point, second: Point) => first.x * second.x + first.y * second.y;

// Отстоянието се мери по осите, не по права линия: при два ъгъла един срещу друг
// то излиза до √2 пъти по-малко от истинското. Затова масите се смятат за
// далечни (без SAT) чак когато кръговете около тях са на 2 × `gap` един от друг.
// Отрицателно `gap` (стар съсед, който масата и досега застъпва) — щом кръговете
// не се допират, масите също не се допират.
const areFarApart = (distance: number, reach: number, otherReach: number, gap: number) =>
  distance >= reach + otherReach + Math.max(gap, 0) * 2;

/**
 * Формата на масата при ъгъл `rotation` (по подразбиране — нейния): кръгла →
 * кръг със столовете наоколо; иначе правоъгълникът на плота, разширен само към
 * страните, на които има столове (`tableChairs`).
 */
export const tableOutline = (table: SchemaElement, rotation = table.rotation): TableOutline => {
  const shape = table.tableShape ?? "round";
  const seats = table.seats ?? 4;
  const { width, height } = tableDimensions(shape, seats, table.size);
  if (shape === "round") {
    const radius = width / 2 + CHAIR_REACH;
    return { kind: "circle", radius, reach: radius };
  }
  const chairs = tableChairs(shape, seats, width, height);
  const reachIf = (hasChair: boolean) => (hasChair ? CHAIR_REACH : 0);
  const left = -width / 2 - reachIf(chairs.some((chair) => chair.x < 0));
  const right = width / 2 + reachIf(chairs.some((chair) => chair.x > width));
  const top = -height / 2 - reachIf(chairs.some((chair) => chair.y < 0));
  const bottom = height / 2 + reachIf(chairs.some((chair) => chair.y > height));
  // CSS rotate() е по часовника: x' = x·cos − y·sin, y' = x·sin + y·cos.
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const corners = [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ].map((corner) => ({ x: corner.x * cos - corner.y * sin, y: corner.x * sin + corner.y * cos }));
  // Две нормали са достатъчни — срещуположните страни са успоредни.
  const normals = [
    { x: -sin, y: cos },
    { x: cos, y: sin },
  ];
  const reach = Math.max(...corners.map((corner) => Math.hypot(corner.x, corner.y)));
  return { kind: "polygon", corners, normals, reach };
};

const projectOn = (outline: TableOutline, center: Point, axis: Point) => {
  const middle = dot(center, axis);
  if (outline.kind === "circle") return { min: middle - outline.radius, max: middle + outline.radius };
  let min = Infinity;
  let max = -Infinity;
  for (const corner of outline.corners) {
    const value = middle + dot(corner, axis);
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return { min, max };
};

const edgeNormals = (outline: TableOutline): Point[] => (outline.kind === "polygon" ? outline.normals : []);

// При кръг срещу правоъгълник — и оста от центъра на кръга към най-близкия ъгъл.
const circleCornerAxis = (circleCenter: Point, polygon: TableOutline, polygonCenter: Point): Point[] => {
  if (polygon.kind !== "polygon") return [];
  const nearest = polygon.corners
    .map((corner) => ({ x: polygonCenter.x + corner.x - circleCenter.x, y: polygonCenter.y + corner.y - circleCenter.y }))
    .reduce((best, candidate) => (Math.hypot(candidate.x, candidate.y) < Math.hypot(best.x, best.y) ? candidate : best));
  const length = Math.hypot(nearest.x, nearest.y);
  return length > EPSILON ? [{ x: nearest.x / length, y: nearest.y / length }] : [];
};

/**
 * С колко да се избута масата с форма `outline` в `center`, за да стои на `gap`
 * от съседа (на по-малкото `obstacle.gap` за стар съсед) — по оста с най-малко
 * застъпване. null — вече е достатъчно далеч.
 */
export const separationFrom = (
  outline: TableOutline,
  center: Point,
  obstacle: TableObstacle,
  requiredGap: number = MIN_TABLE_GAP,
): Point | null => {
  const gap = Math.min(requiredGap, obstacle.gap ?? requiredGap);
  const obstacleCenter = { x: obstacle.element.x, y: obstacle.element.y };
  const centerDistance = Math.hypot(center.x - obstacleCenter.x, center.y - obstacleCenter.y);
  if (areFarApart(centerDistance, outline.reach, obstacle.outline.reach, gap)) return null;
  if (outline.kind === "circle" && obstacle.outline.kind === "circle") {
    const deltaX = center.x - obstacleCenter.x;
    const deltaY = center.y - obstacleCenter.y;
    const distance = Math.hypot(deltaX, deltaY);
    const needed = outline.radius + obstacle.outline.radius + gap;
    if (distance >= needed - EPSILON) return null;
    // Точно един върху друг — посоката е без значение, избираме надясно.
    const direction = distance > EPSILON ? { x: deltaX / distance, y: deltaY / distance } : { x: 1, y: 0 };
    return { x: direction.x * (needed - distance), y: direction.y * (needed - distance) };
  }
  const axes = [
    ...edgeNormals(outline),
    ...edgeNormals(obstacle.outline),
    ...SCREEN_AXES,
    ...(outline.kind === "circle" ? circleCornerAxis(center, obstacle.outline, obstacleCenter) : []),
    ...(obstacle.outline.kind === "circle" ? circleCornerAxis(obstacleCenter, outline, center) : []),
  ];
  let best: Point | null = null;
  let bestDepth = Infinity;
  for (const axis of axes) {
    const moving = projectOn(outline, center, axis);
    const other = projectOn(obstacle.outline, obstacleCenter, axis);
    // Колко да се мръдне по оста напред / назад, за да остане `gap` между двете.
    const forward = other.max + gap - moving.min;
    const backward = moving.max + gap - other.min;
    if (forward <= EPSILON || backward <= EPSILON) return null;
    const depth = Math.min(forward, backward);
    if (depth < bestDepth) {
      bestDepth = depth;
      const sign = forward <= backward ? 1 : -1;
      best = { x: axis.x * depth * sign, y: axis.y * depth * sign };
    }
  }
  return best;
};

// По-близо от `gap` (с търпимостта за закръглянето до цели единици).
const isTooClose = (outline: TableOutline, center: Point, obstacle: TableObstacle, gap: number) => {
  const push = separationFrom(outline, center, obstacle, gap);
  return push !== null && Math.hypot(push.x, push.y) > OVERLAP_TOLERANCE;
};

// Останалите маси (без самата маса) — всяка с пълното MIN_TABLE_GAP.
const otherTables = (table: SchemaElement, elements: SchemaElement[]): TableObstacle[] =>
  elements
    .filter((other) => other.kind === "table" && other.id !== table.id)
    .map((other) => ({ element: other, outline: tableOutline(other) }));

// На какво отстояние е масата в `center` от съседа — по оста, по която са най-далеч
// (до MIN_TABLE_GAP; при застъпване — отрицателно).
const gapBetween = (outline: TableOutline, center: Point, obstacle: TableObstacle) => {
  const push = separationFrom(outline, center, { element: obstacle.element, outline: obstacle.outline });
  return push ? MIN_TABLE_GAP - Math.hypot(push.x, push.y) : MIN_TABLE_GAP;
};

/**
 * Останалите маси като препятствия за `table` (на своето място и със своя ъгъл).
 * Съсед, който и досега е по-близо от MIN_TABLE_GAP (стара схема), носи
 * сегашното отстояние като свое `gap` — масата не може да го доближи повече,
 * но и не е заключена: може да се отдалечи.
 */
export const tableObstacles = (table: SchemaElement, elements: SchemaElement[]): TableObstacle[] => {
  const outline = tableOutline(table);
  return otherTables(table, elements).map((obstacle) => {
    const currentGap = gapBetween(outline, table, obstacle);
    return currentGap < MIN_TABLE_GAP ? { ...obstacle, gap: currentGap } : obstacle;
  });
};

/** Масите, които `table` (на своето място и със своя ъгъл) застъпва или доближава под `gap`. */
export const overlappingTables = (
  table: SchemaElement,
  elements: SchemaElement[],
  gap: number = MIN_TABLE_GAP,
): SchemaElement[] => {
  if (table.kind !== "table") return [];
  const outline = tableOutline(table);
  return otherTables(table, elements)
    .filter((obstacle) => isTooClose(outline, table, obstacle, gap))
    .map((obstacle) => obstacle.element);
};

/**
 * Масата, в която промяната би вкарала `after` (същата маса с новото място,
 * ъгъл, форма, места или размер). Съсед, който `before` и досега е доближавала
 * под MIN_TABLE_GAP (стара схема отпреди правилото), пречи само ако промяната
 * я доближава още повече — положението не става по-лошо, но масата не е
 * заключена. null — позволено.
 */
export const tableBlockingChange = (
  before: SchemaElement,
  after: SchemaElement,
  elements: SchemaElement[],
): SchemaElement | null => {
  if (after.kind !== "table") return null;
  const outline = tableOutline(after);
  const blocker = tableObstacles(before, elements).find((obstacle) =>
    isTooClose(outline, after, obstacle, MIN_TABLE_GAP),
  );
  return blocker?.element ?? null;
};

// Стар съсед, от който масата вече се е отдалечила: оттук нататък не може да
// го доближи повече от новото отстояние; стигне ли MIN_TABLE_GAP — обикновен съсед.
const widenLegacyGap = (outline: TableOutline, center: Point, obstacle: TableObstacle): TableObstacle => {
  if (obstacle.gap === undefined) return obstacle;
  const reachedGap = gapBetween(outline, center, obstacle);
  if (reachedGap >= MIN_TABLE_GAP) return { element: obstacle.element, outline: obstacle.outline };
  return reachedGap > obstacle.gap ? { ...obstacle, gap: reachedGap } : obstacle;
};

// Избутва масата от всички съседи; null — заклещена е (между две маси няма място).
const pushOut = (outline: TableOutline, start: Point, obstacles: TableObstacle[], gap: number) => {
  let position = start;
  for (let attempt = 0; attempt <= MAX_PUSHES; attempt++) {
    let deepest: Point | null = null;
    for (const obstacle of obstacles) {
      const push = separationFrom(outline, position, obstacle, gap);
      if (push && (!deepest || Math.hypot(push.x, push.y) > Math.hypot(deepest.x, deepest.y))) deepest = push;
    }
    if (!deepest) return position;
    if (attempt === MAX_PUSHES) return null;
    position = { x: position.x + deepest.x, y: position.y + deepest.y };
  }
  return null;
};

const distanceToSegment = (point: Point, from: Point, to: Point) => {
  const segment = { x: to.x - from.x, y: to.y - from.y };
  const lengthSquared = dot(segment, segment);
  const along =
    lengthSquared > 0
      ? Math.max(0, Math.min(1, ((point.x - from.x) * segment.x + (point.y - from.y) * segment.y) / lengthSquared))
      : 0;
  return Math.hypot(from.x + segment.x * along - point.x, from.y + segment.y * along - point.y);
};

// Един опит от `from` към `to` на стъпки по SLIDE_STEP: всяка стъпка, която влиза
// в съсед, се избутва обратно по оста с най-малко застъпване — нормалната част
// на движението се губи, допирателната остава, тоест масата се плъзга покрай съседа.
// Плъзгайки се, масата се отклонява от правата, затова избутването гледа ВСИЧКИ
// маси; правата решава само дали пътят е чист изобщо (нищо до нея → направо `to`).
const slidePass = (outline: TableOutline, from: Point, to: Point, obstacles: TableObstacle[], gap: number) => {
  const isPathClear = obstacles.every((obstacle) =>
    areFarApart(distanceToSegment(obstacle.element, from, to), outline.reach, obstacle.outline.reach, gap),
  );
  if (isPathClear) return { position: to, blocked: false };
  const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / SLIDE_STEP));
  const step = { x: (to.x - from.x) / steps, y: (to.y - from.y) / steps };
  let position = from;
  let blocked = false;
  for (let index = 0; index < steps; index++) {
    const candidate = { x: position.x + step.x, y: position.y + step.y };
    const resolved = pushOut(outline, candidate, obstacles, gap);
    if (!resolved) return { position, blocked: true };
    if (resolved !== candidate) blocked = true;
    position = resolved;
  }
  return blocked ? { position, blocked } : { position: to, blocked };
};

/**
 * Къде застава маса с форма `outline`, тръгнала от `from` (последното ѝ
 * свободно място) към `to` (под пръста): точно в `to`, ако пътят е чист; иначе
 * опира в съседа на `gap` от него и се плъзга покрай ръба му към `to` — никога
 * не минава през него и не стъпва върху него. След като заобиколи ъгъл, тръгва
 * наново към `to` (до MAX_SLIDE_PASSES пъти). Резултатът е в цели единици.
 */
export const slideTable = (
  outline: TableOutline,
  from: Point,
  to: Point,
  obstacles: TableObstacle[],
  gap: number = MIN_TABLE_GAP,
): Point => {
  let position = from;
  for (let pass = 0; pass < MAX_SLIDE_PASSES; pass++) {
    const result = slidePass(outline, position, to, obstacles, gap);
    if (!result.blocked) return to;
    const moved = Math.hypot(result.position.x - position.x, result.position.y - position.y);
    position = result.position;
    if (moved < 0.5) break;
  }
  return { x: Math.round(position.x), y: Math.round(position.y) };
};

// Докъде около опрялата маса се търси свободна точка на решетката (в стъпки).
const GRID_SEARCH_STEPS = 2;

// Точките на решетката около `value` (± GRID_SEARCH_STEPS стъпки).
const gridValuesAround = (value: number) => {
  const below = Math.floor(value / GRID_STEP) * GRID_STEP;
  const values: number[] = [];
  for (let offset = -GRID_SEARCH_STEPS; offset <= GRID_SEARCH_STEPS + 1; offset++) {
    values.push(below + offset * GRID_STEP);
  }
  return values;
};

/**
 * Масата е опряла в съсед и стои в `position` — извън решетката (367 вместо
 * 360). Застава на най-близката точка на решетката, на която е на поне `gap`
 * от всички съседи — по-далеч от съседа, никога в него. Ос, по която масата е
 * точно там, където я иска пръстът (`target`, вече на решетката или подравнен
 * към съсед), остава както е. Няма свободна точка наблизо → `position` (тя е
 * свободна).
 */
export const snapToGridClearOfTables = (
  outline: TableOutline,
  position: Point,
  target: Point,
  obstacles: TableObstacle[],
  gap: number = MIN_TABLE_GAP,
): Point => {
  const valuesAlong = (value: number, wanted: number) =>
    value === wanted ? [value, ...gridValuesAround(value)] : gridValuesAround(value);
  const valuesY = valuesAlong(position.y, target.y);
  const distanceFromPosition = (candidate: Point) => Math.hypot(candidate.x - position.x, candidate.y - position.y);
  const candidates = valuesAlong(position.x, target.x)
    .flatMap((x) => valuesY.map((y) => ({ x, y })))
    .sort((first, second) => distanceFromPosition(first) - distanceFromPosition(second));
  // Строго — без търпимостта за закръглянето: точката на решетката е избрана, не закръглена.
  const isClear = (candidate: Point) =>
    obstacles.every((obstacle) => separationFrom(outline, candidate, obstacle, gap) === null);
  return candidates.find(isClear) ?? position;
};

/** Докъде се завъртя масата и коя маса я спря (null — стигна целта). */
export interface TableRotationStop {
  rotation: number;
  blocker: SchemaElement | null;
}

/**
 * Завъртане на масата от `from` към `to` градуса по по-краткия път, градус по
 * градус, докато не опре в съсед: последният свободен ЦЯЛ ъгъл (0–359) и
 * съседът, който я спря. Пътят е чист → точно `to`, без съсед. Закръглянето
 * на спрелия ъгъл не бива да стъпи към съседа — пускането проверява наново
 * (`tableBlockingChange`) и би върнало масата в началото.
 */
export const rotateTableToward = (
  table: SchemaElement,
  from: number,
  to: number,
  obstacles: TableObstacle[],
  gap: number = MIN_TABLE_GAP,
): TableRotationStop => {
  const delta = ((((to - from) % 360) + 540) % 360) - 180;
  const steps = Math.ceil(Math.abs(delta));
  const blockerAt = (rotation: number) => {
    const outline = tableOutline(table, rotation);
    return obstacles.find((obstacle) => isTooClose(outline, table, obstacle, gap))?.element ?? null;
  };
  let reached = from;
  for (let index = 1; index <= steps; index++) {
    const rotation = index === steps ? from + delta : from + Math.sign(delta) * index;
    const blocker = blockerAt(rotation);
    if (blocker) {
      const rounded = Math.round(reached);
      const backward = delta > 0 ? Math.floor(reached) : Math.ceil(reached);
      const settled = blockerAt(rounded) === null ? rounded : blockerAt(backward) === null ? backward : reached;
      return { rotation: ((settled % 360) + 360) % 360, blocker };
    }
    reached = rotation;
  }
  return { rotation: to, blocker: null };
};

/**
 * Едно влачене на маса: всяко движение на пръста (`target`, вече подравнено)
 * дава къде застава масата — от последното ѝ място към пръста, с плъзгане
 * покрай съседите (`slideTable`). Опряла в съсед, тя застава на решетката, на
 * която е и пръстът (`snapToGridClearOfTables`). Маса, която още в началото е
 * твърде близо до съсед (стара схема), може да се отдалечи от него, но не и да
 * го доближи повече; щом стигне MIN_TABLE_GAP, и той е обикновено препятствие.
 */
export const createTableDragGuard = (table: SchemaElement, elements: SchemaElement[]) => {
  const outline = tableOutline(table);
  let obstacles = tableObstacles(table, elements);
  let position: Point = { x: table.x, y: table.y };
  return (target: Point): Point => {
    const reached = slideTable(outline, position, target, obstacles);
    const isHeldBack = reached.x !== target.x || reached.y !== target.y;
    const settled = isHeldBack ? snapToGridClearOfTables(outline, reached, target, obstacles) : reached;
    position = { x: settled.x, y: settled.y };
    if (obstacles.some((obstacle) => obstacle.gap !== undefined)) {
      obstacles = obstacles.map((obstacle) => widenLegacyGap(outline, position, obstacle));
    }
    return position;
  };
};

/**
 * Едно завъртане на маса с дръжката: всеки нов ъгъл под курсора (`target`)
 * дава докъде масата наистина се завърта — до първия съсед по пътя
 * (`rotateTableToward`) — и кой съсед я спира (null — масата е стигнала
 * курсора). Съсед, до който е твърде близо още в началото (стара схема), пречи
 * само ако завъртането я доближава още повече.
 */
export const createTableRotationGuard = (table: SchemaElement, elements: SchemaElement[]) => {
  const obstacles = tableObstacles(table, elements);
  let rotation = table.rotation;
  return (target: number): TableRotationStop => {
    const stop = rotateTableToward(table, rotation, target, obstacles);
    rotation = stop.rotation;
    return stop;
  };
};
